export const OOXML_PARTS = {
    DOCUMENT: 'word/document.xml',
    STYLES: 'word/styles.xml',
    NUMBERING: 'word/numbering.xml',
} as const;

/** OOXML sentinel for "no explicit color" (w:color/@w:val, w:shd/@w:fill). */
export const OOXML_AUTO = 'auto';

/** w:numPr/w:numId val="0" means "this paragraph has NO numbering" (cancels a style-inherited numPr). */
export const NUM_ID_NONE = '0';

export const OOXML_LIST_STYLE_TYPES = {
    DISC: 'disc',
    CIRCLE: 'circle',
    SQUARE: 'square',
    DECIMAL: 'decimal',
    DECIMAL_LEADING_ZERO: 'decimal-leading-zero',
    LOWER_ALPHA: 'lower-alpha',
    UPPER_ALPHA: 'upper-alpha',
    LOWER_ROMAN: 'lower-roman',
    UPPER_ROMAN: 'upper-roman',
} as const;

/**
 * w:numFmt → Plate listStyleType for ordered lists. Unknown ordered formats
 * (ordinalText, chicago, …) fall back to DECIMAL at the call site.
 */
export const NUM_FMT_TO_LIST_STYLE: Record<string, string> = {
    decimal: OOXML_LIST_STYLE_TYPES.DECIMAL,
    decimalZero: OOXML_LIST_STYLE_TYPES.DECIMAL_LEADING_ZERO,
    lowerLetter: OOXML_LIST_STYLE_TYPES.LOWER_ALPHA,
    upperLetter: OOXML_LIST_STYLE_TYPES.UPPER_ALPHA,
    lowerRoman: OOXML_LIST_STYLE_TYPES.LOWER_ROMAN,
    upperRoman: OOXML_LIST_STYLE_TYPES.UPPER_ROMAN,
};

/**
 * Bullet lvlText first-glyph → Plate listStyleType. Dash bullets (- – —) map
 * to disc because Plate's listStyleType set has no dash marker — disc is the
 * chosen nearest. Anything unmapped (Wingdings private-use chars, etc.) also
 * falls back to disc at the call site.
 */
export const BULLET_GLYPH_TO_LIST_STYLE: Record<string, string> = {
    '•': OOXML_LIST_STYLE_TYPES.DISC,
    '●': OOXML_LIST_STYLE_TYPES.DISC,
    '◦': OOXML_LIST_STYLE_TYPES.CIRCLE,
    '○': OOXML_LIST_STYLE_TYPES.CIRCLE,
    '▪': OOXML_LIST_STYLE_TYPES.SQUARE,
    '■': OOXML_LIST_STYLE_TYPES.SQUARE,
    '□': OOXML_LIST_STYLE_TYPES.SQUARE,
    '-': OOXML_LIST_STYLE_TYPES.DISC,
    '–': OOXML_LIST_STYLE_TYPES.DISC,
    '—': OOXML_LIST_STYLE_TYPES.DISC,
};

export const DEFAULT_LIST_START = 1;

/**
 * Upper bound for any parsed/derived list start or ordinal (w:start,
 * w:startOverride, or a running counter value). Guards against a hostile or
 * malformed numbering.xml driving an absurd `counter-reset`/`<ol start>`.
 */
export const MAX_LIST_START = 100000;

/** ST_HighlightColor names (w:highlight/@w:val) → hex. */
export const HIGHLIGHT_NAME_TO_HEX: Record<string, string> = {
    yellow: '#FFFF00',
    green: '#00FF00',
    cyan: '#00FFFF',
    magenta: '#FF00FF',
    blue: '#0000FF',
    red: '#FF0000',
    darkBlue: '#00008B',
    darkCyan: '#008B8B',
    darkGreen: '#006400',
    darkMagenta: '#8B008B',
    darkRed: '#8B0000',
    darkYellow: '#808000',
    darkGray: '#A9A9A9',
    lightGray: '#D3D3D3',
    black: '#000000',
};

/** w:jc/@w:val → Plate align. left/start (the default) are deliberately absent. */
export const JC_TO_ALIGN: Record<string, 'center' | 'right' | 'justify'> = {
    center: 'center',
    right: 'right',
    end: 'right',
    both: 'justify',
    distribute: 'justify',
};

export const LINE_RULE_AUTO = 'auto';

/** w:spacing/@w:line with lineRule "auto" is in 240ths of a line. */
export const TWIPS_PER_LINE = 240;

export const LINE_HEIGHT_MIN = 1;
export const LINE_HEIGHT_MAX = 3;

// The editor's own default line-height (LineHeightMenu defaultValue). We only
// re-attach an OOXML lineHeight when it's LOOSER than this — Word's default is
// single (1.0), and stamping that would make imported text tighter than the
// editor's baseline. Deliberate 1.5/2.0 spacing (> default) is still honored.
export const EDITOR_DEFAULT_LINE_HEIGHT = 1.2;

/** sz is half-points; repo fontSize convention is px strings (pt → px = ×4/3). */
export const PX_PER_PT = 4 / 3;

export const STYLE_TYPES = {
    PARAGRAPH: 'paragraph',
    CHARACTER: 'character',
} as const;

export type OoxmlStyleType = (typeof STYLE_TYPES)[keyof typeof STYLE_TYPES];
